// The registry's settings screens, as the renderer's model (spec/03 §9, spec/13 §13, spec/14 §14;
// F-ORG-02, F-HQ-38).
//
// `GET /v1/organizations/{o}/venues/{v}/settings` (and the organisation's, and HQ's two) returns every
// setting the screen shows with everything the renderer needs — its group, JSON Schema, value and
// source, what a reset gives, its lock and where it may be locked, and whether this caller may change
// it — so nothing here knows any setting by name. `editable` is the server's answer; the console only
// draws it.
//
// Four screens, one renderer:
//
//   venue           GET/PUT /v1/organizations/{o}/venues/{v}/settings      the venue console
//   organization    GET/PUT /v1/organizations/{o}/settings                  the organisation console
//   platform        GET/PUT /v1/hq/settings                                  HQ: platform defaults, locks
//   hqOrganization  GET/PUT /v1/hq/organizations/{o}/settings                HQ: one organisation's, locks
//
// On HQ's two, a setting lockable at the screen's own scope can be locked there (`lockable`), and a
// lock there is HQ's own: it binds the scopes below and leaves the value editable here.

import type { ApiClient, SettingEntry, SettingsView } from '@clavian/api-client';
import { SETTING_UNIT_KEYWORD } from '@clavian/contracts/constants';
import type { VenueFormatter } from '@clavian/i18n';

import { queryKeys } from '../api/queries';
import { versioned, type Versioned } from '../api/versioned';
import { DEFAULT_COUNTRY } from '../i18n';
import { placementOf } from './layout';
import { settingMoney, type SettingMoney } from './money';
import type { JsonSchema } from './schema';
import type { RenderableSetting, SettingChange, SettingsPart } from './types';

export type SettingsScope =
  | { readonly kind: 'venue'; readonly organizationId: string; readonly venueId: string }
  | { readonly kind: 'organization'; readonly organizationId: string }
  | { readonly kind: 'platform' }
  | { readonly kind: 'hqOrganization'; readonly organizationId: string };

/** The scope an HQ screen stores its values at, and may lock them at; none on staff's screens. */
export function hqScopeOf(scope: SettingsScope): 'platform' | 'organization' | undefined {
  if (scope.kind === 'platform') return 'platform';
  if (scope.kind === 'hqOrganization') return 'organization';
  return undefined;
}

/** The query a settings screen is cached under — shared by the screen and the palette's settings search. */
export function settingsKey(scope: SettingsScope): readonly unknown[] {
  switch (scope.kind) {
    case 'venue':
      return queryKeys.venueSettings(scope.organizationId, scope.venueId);
    case 'organization':
      return queryKeys.organizationSettings(scope.organizationId);
    case 'platform':
      return queryKeys.platformSettings;
    case 'hqOrganization':
      return queryKeys.hqOrganizationSettings(scope.organizationId);
  }
}

export function readSettings(api: ApiClient, scope: SettingsScope): Promise<Versioned<SettingsView>> {
  return versioned((onResponse) => {
    switch (scope.kind) {
      case 'venue':
        return api.getVenueSettings({ path: { organizationId: scope.organizationId, venueId: scope.venueId }, onResponse });
      case 'organization':
        return api.getOrganizationSettings({ path: { organizationId: scope.organizationId }, onResponse });
      case 'platform':
        return api.getPlatformSettings({ onResponse });
      case 'hqOrganization':
        return api.getHqOrganizationSettings({ path: { organizationId: scope.organizationId }, onResponse });
    }
  });
}

/** A change as the wire has it: a value, `reset: true`, and on HQ's screens a lock. */
function wireChange(change: SettingChange) {
  if (change.reset) return { key: change.key, reset: true as const };
  return { key: change.key, value: change.value, ...(change.locked === undefined ? {} : { locked: change.locked }) };
}

export function writeSettings(
  api: ApiClient,
  scope: SettingsScope,
  changes: readonly SettingChange[],
  etag: string,
): Promise<Versioned<SettingsView>> {
  const headerParams = { 'if-match': etag };
  return versioned((onResponse) => {
    switch (scope.kind) {
      case 'venue':
        return api.putVenueSettings({
          path: { organizationId: scope.organizationId, venueId: scope.venueId },
          headerParams,
          body: { changes: changes.map((change) => (change.reset ? { key: change.key, reset: true } : { key: change.key, value: change.value })) },
          onResponse,
        });
      case 'organization':
        return api.putOrganizationSettings({
          path: { organizationId: scope.organizationId },
          headerParams,
          body: { changes: changes.map((change) => (change.reset ? { key: change.key, reset: true } : { key: change.key, value: change.value })) },
          onResponse,
        });
      case 'platform':
        return api.putPlatformSettings({ headerParams, body: { changes: changes.map(wireChange) }, onResponse });
      case 'hqOrganization':
        return api.putHqOrganizationSettings({
          path: { organizationId: scope.organizationId },
          headerParams,
          body: { changes: changes.map(wireChange) },
          onResponse,
        });
    }
  });
}

export interface RegistryWords {
  /** The setting's label: the console's copy for its key, or its name made readable. */
  readonly label: (key: string) => string;
  /**
   * The one line under the label: the console's copy for its key. Without it the registry's own description is a
   * developer's fallback — a test fails any registered setting that has no copy (`settings-layout.test.ts`).
   */
  readonly help?: (key: string) => string | undefined;
  /** A layout section's sub-heading (`layout.ts`). */
  readonly section?: (id: string) => string;
  /** A station class of this venue, by id: its name (per-class values, spec/13 §12). */
  readonly className?: (id: string) => string | undefined;
  /** A venue of the organisation, by id: its name (the organisation's screen). */
  readonly venueName?: (id: string) => string | undefined;
  /**
   * HQ's screens: why a setting shown there cannot be set there — it is not set at that scope at all
   * (a venue's power schedule on the platform's screen).
   */
  readonly notSetHere?: string;
}

export interface RenderOptions {
  /** On HQ's screens, the scope the screen stores values at — and locks them at. */
  readonly hqScope?: 'platform' | 'organization';
  /** The screen's currency and the formatter's words for it, for money settings (`unit: 'money'`). */
  readonly money?: SettingMoney;
  /** The screen it is drawn on: a venue's and an organisation's leave a setting with a home elsewhere to it. */
  readonly screen?: SettingsScope['kind'];
}

/** Scopes inside a venue: a value stored there is a class's or a zone's. */
const INSIDE_VENUE: readonly string[] = ['stationClass', 'zone', 'station'];

export function toRenderable(entry: SettingEntry, words: RegistryWords, options: RenderOptions = {}): RenderableSetting {
  // A setting's own unit (`unit: 'money'`) is its value's: the control reads it from the schema, where
  // a field inside a structured value carries it too (`x-clavian-unit`).
  const schema = (entry.unit === undefined ? entry.schema : { ...entry.schema, [SETTING_UNIT_KEYWORD]: entry.unit }) as JsonSchema;
  const defs = (schema['$defs'] ?? schema['definitions'] ?? {}) as Record<string, JsonSchema>;
  const { hqScope, money } = options;
  // HQ's own lock, at the screen's scope: it does not bind the screen that holds it.
  const lockedHere = hqScope !== undefined && entry.lockedAt === hqScope;
  const editable = entry.editable && (entry.lockedAt === undefined || lockedHere);
  const notSetHere = hqScope !== undefined && !entry.scopes.includes(hqScope);
  const placement = placementOf(entry.key, entry.group);
  const help = words.help?.(entry.key) ?? entry.description;
  // Why a row cannot be changed here, in the row's own words (a lock says its own).
  const why: RenderableSetting['why'] = entry.dated
    ? 'dated'
    : editable || entry.lockedAt !== undefined || hqScope !== undefined
      ? undefined
      : options.screen === 'venue' && !entry.scopes.some((scope) => scope === 'venue' || INSIDE_VENUE.includes(scope))
        ? 'organization'
        : 'role';
  const classValues = entry.overrides.flatMap((place) => {
    if (place.scopeType !== 'stationClass' || place.stationClassId === null) return [];
    return [{ name: words.className?.(place.stationClassId) ?? place.stationClassId, value: place.value }];
  });
  const venueValues = (entry.venueOverrides ?? []).map((place) => ({ name: words.venueName?.(place.venueId) ?? place.venueId, value: place.value }));
  return {
    key: entry.key,
    label: words.label(entry.key),
    description: help,
    schema,
    context: { defs, defaultCountry: DEFAULT_COUNTRY, ...(money === undefined ? {} : { money }) },
    value: entry.value,
    source: entry.source,
    ...(entry.lockedAt === undefined ? {} : { lockedAt: entry.lockedAt }),
    ...(entry.hasOverride ? { inherited: { value: entry.inherited.value, source: entry.inherited.source } } : {}),
    hasOverride: entry.hasOverride,
    // A lock above the screen binds whoever is looking; the server says so in `editable` too, and the
    // console never offers what it has been told is refused.
    editable,
    // Lockable where the specification marks it (`lockableAt`), by someone who may change it here.
    ...(hqScope !== undefined && editable && entry.lockableAt.includes(hqScope) ? { lockable: hqScope } : {}),
    ...(notSetHere && words.notSetHere !== undefined ? { readOnlyReason: words.notSetHere } : {}),
    placeOverrides: entry.overrides.length,
    ...(entry.unit === undefined ? {} : { unitDefault: { value: entry.default } }),
    sectionId: placement.section,
    order: placement.order,
    ...(placement.advanced === true ? { advanced: true } : {}),
    ...(placement.dependsOn === undefined ? {} : { dependsOn: placement.dependsOn }),
    ...(placement.overriddenBy === undefined ? {} : { overriddenBy: placement.overriddenBy }),
    ...(placement.under === undefined ? {} : { under: placement.under }),
    ...(placement.display === undefined ? {} : { display: placement.display }),
    ...(placement.control === undefined ? {} : { control: placement.control }),
    ...(words.section === undefined ? {} : { section: words.section(placement.section) }),
    ...(why === undefined ? {} : { why }),
    ...(classValues.length === 0 ? {} : { classValues }),
    ...(venueValues.length === 0 ? {} : { venueValues }),
  };
}

/**
 * Is this entry drawn as a row of its group on this screen? A setting whose home is another panel is not, on the
 * screens that have that panel: pay-as-you-go is edited in Pricing (venue and organisation), the venue's tax mode and
 * GSTIN in its tax registration (venue).
 */
export function drawnAsRow(key: string, registryGroup: string, screen: SettingsScope['kind']): boolean {
  const { home } = placementOf(key, registryGroup);
  if (home === 'pricing') return screen !== 'venue' && screen !== 'organization';
  if (home === 'taxRegistration') return screen !== 'venue';
  return true;
}

/**
 * The rows of one job group (`layout.ts`), in the layout's order: every setting the view lists that is placed in the
 * group and drawn as a row on this screen.
 */
export function groupEntries(view: SettingsView, group: string, words: RegistryWords, options: RenderOptions = {}): RenderableSetting[] {
  const screen = options.screen ?? 'venue';
  return view.settings
    .filter((entry) => placementOf(entry.key, entry.group).group === group && drawnAsRow(entry.key, entry.group, screen))
    .map((entry) => toRenderable(entry, words, options))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.key.localeCompare(b.key));
}

/** The job groups this view has a row in, on this screen. */
export function groupsWithRows(view: SettingsView, screen: SettingsScope['kind']): Set<string> {
  return new Set(view.settings.filter((entry) => drawnAsRow(entry.key, entry.group, screen)).map((entry) => placementOf(entry.key, entry.group).group));
}

/** `changes.2.value.wakeLeadMinutes` → `venue.powerSchedule.wakeLeadMinutes`. */
export function registryFieldKey(field: string, changes: readonly SettingChange[]): string | undefined {
  const match = /^changes\.(\d+)(?:\.(?:value|key|reset|locked))?(?:\.(.*))?$/.exec(field);
  if (match === null) return undefined;
  const change = changes[Number(match[1])];
  if (change === undefined) return undefined;
  return match[2] === undefined ? change.key : `${change.key}.${match[2]}`;
}

/** One group of a settings screen, as a part of a settings form. */
export function registryPart(options: {
  readonly api: ApiClient;
  readonly scope: SettingsScope;
  readonly group: string;
  readonly view: Versioned<SettingsView>;
  readonly words: RegistryWords;
  /** Told of every fresh version, so the query cache holds what the form holds. */
  readonly onVersion: (view: Versioned<SettingsView>) => void;
  /** The console's formatter: money settings read in the view's currency (`scope.currency`) with it. */
  readonly formatter?: VenueFormatter;
}): SettingsPart {
  const { api, scope, group, words, onVersion, formatter } = options;
  const hqScope = hqScopeOf(scope);
  // Money settings are in the screen's currency — the organisation's; INR, the platform's, on HQ's
  // platform screen — which the view names; a screen whose view did not would show plain integers.
  const currency = options.view.data.scope.currency;
  const render: RenderOptions = {
    screen: scope.kind,
    ...(hqScope === undefined ? {} : { hqScope }),
    ...(formatter === undefined || !currency ? {} : { money: settingMoney(formatter, currency) }),
  };
  const entriesOf = (view: Versioned<SettingsView>) => ({ data: groupEntries(view.data, group, words, render), etag: view.etag });
  return {
    id: 'registry',
    entries: groupEntries(options.view.data, group, words, render),
    etag: options.view.etag,
    save: async (changes, etag) => {
      const next = await writeSettings(api, scope, changes, etag);
      onVersion(next);
      return entriesOf(next);
    },
    reload: async () => {
      const next = await readSettings(api, scope);
      onVersion(next);
      return entriesOf(next);
    },
    fieldKey: registryFieldKey,
  };
}
